// CourierFormXL - Sprint 44 Fase 3 Vista 6.
// Form de courier (alta/edicion) con tokens. Reusa XLInput + XLCheck.
// Preview de URL final = url_base + 'ABC123'. Botones tokenizados.
import { useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import XLInput from '../../components/xl/XLInput';
import XLCheck from '../../components/xl/XLCheck';
import { IconPlus } from './components/Icons';

const CSS_ID = 'xl-cou-form-css';
const CSS = `
.xl-cou-form {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  font-family: var(--xl-font-sans);
}
.xl-cou-form__hd {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  font-size: 13px; font-weight: 600;
  color: var(--xl-color-text);
}
.xl-cou-form__hd-ico { color: var(--xl-color-info) }
.xl-cou-form__body {
  padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
}
.xl-cou-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
@media (max-width: 480px) { .xl-cou-form__row { grid-template-columns: 1fr } }

.xl-cou-form__hint {
  font-size: 11px; color: var(--xl-color-text-muted); line-height: 1.45;
}
.xl-cou-form__hint code {
  background: var(--xl-color-surface-alt);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: var(--xl-font-mono);
  font-size: 10.5px;
}
.xl-cou-form__preview {
  background: rgba(5,150,105,0.08);
  border: 1px solid rgba(5,150,105,0.28);
  border-radius: var(--xl-radius-sm);
  padding: 8px 10px;
  font-size: 11px;
}
[data-xl-theme="dark"] .xl-cou-form__preview {
  background: rgba(16,185,129,0.14);
  border-color: rgba(16,185,129,0.32);
}
.xl-cou-form__preview-lbl { color: var(--xl-color-success); font-weight: 500 }
.xl-cou-form__preview-url {
  color: var(--xl-color-success);
  font-family: var(--xl-font-mono);
  word-break: break-all;
  display: block;
  margin-top: 2px;
}

.xl-cou-form__err {
  font-size: 12px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
  border-radius: var(--xl-radius-sm);
  padding: 8px 12px;
}

.xl-cou-form__actions {
  display: flex; gap: 8px; padding-top: 4px;
}
.xl-cou-form__btn {
  font: 500 13px/1 var(--xl-font-sans);
  padding: 10px 16px;
  border-radius: var(--xl-radius-md);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-cou-form__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-cou-form__btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-cou-form__btn--primary {
  flex: 1;
  background: var(--xl-color-primary);
  color: var(--xl-color-text-inverse);
}
.xl-cou-form__btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-cou-form__btn--ghost {
  background: transparent;
  color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-cou-form__btn--ghost:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
`;

const emptyForm = {
  id: null,
  nombre: '',
  url_base_tracking: '',
  pais: '',
  logo_url: '',
  is_active: true,
};

export default function CourierFormXL({ initial, onSubmit, onCancel }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nombre || !form.nombre.trim()) {
      setErr('Falta el nombre');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSubmit(form);
      if (!form.id) setForm(emptyForm);
    } catch (e) {
      setErr((e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = (form.url_base_tracking || '') + 'ABC123';

  return (
    <div className="xl-cou-form">
      <div className="xl-cou-form__hd">
        <span className="xl-cou-form__hd-ico"><IconPlus /></span>
        {form.id ? 'Editar courier' : 'Nuevo courier'}
      </div>
      <div className="xl-cou-form__body">
        <XLInput
          label="Nombre *"
          value={form.nombre || ''}
          onChange={(e) => set('nombre', e.target.value)}
          placeholder="DHL Express"
        />
        <div className="xl-cou-form__row">
          <XLInput
            label="Pais"
            value={form.pais || ''}
            onChange={(e) => set('pais', e.target.value)}
            placeholder="USA"
          />
          <XLCheck
            checked={!!form.is_active}
            onChange={(v) => set('is_active', v)}
            label="Activo"
          />
        </div>
        <XLInput
          label="Logo URL (opcional)"
          value={form.logo_url || ''}
          onChange={(e) => set('logo_url', e.target.value)}
          placeholder="https://..."
        />
        <XLInput
          label="URL base de rastreo (opcional)"
          value={form.url_base_tracking || ''}
          onChange={(e) => set('url_base_tracking', e.target.value)}
          placeholder="https://courier.com/track?id="
        />
        <p className="xl-cou-form__hint">
          URL fija a la que se concatena el numero de tracking.
          Terminala en <code>/</code>, <code>=</code> o lo que corresponda.
          En la importacion: URL final = URL base + numero de tracking.
        </p>
        {form.url_base_tracking && (
          <div className="xl-cou-form__preview" role="status">
            <span className="xl-cou-form__preview-lbl">
              Ejemplo con tracking ABC123:
            </span>
            <span className="xl-cou-form__preview-url">{previewUrl}</span>
          </div>
        )}
        {err && (
          <div className="xl-cou-form__err" role="alert">{err}</div>
        )}
        <div className="xl-cou-form__actions">
          <button type="button"
            className="xl-cou-form__btn xl-cou-form__btn--primary"
            onClick={handleSubmit}
            disabled={saving || !form.nombre}>
            {saving ? 'Guardando\u2026' : (form.id ? 'Guardar cambios' : 'Crear courier')}
          </button>
          {form.id && (
            <button type="button"
              className="xl-cou-form__btn xl-cou-form__btn--ghost"
              onClick={onCancel}>
              Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
